import { AnnouncementBase } from '.';
import { $ } from '../../../../vendor/jquery';
import { AnnouncementWidget } from '../AnnouncementWidget';
import { DEFAULT_BACKGROUND_COLOR } from '../types';
import {
    defaultTemplate,
    insufficientPlaylistRightsTemplate,
    videoNotSupportedTemplate,
} from '../Templates';
import { toBoolean, toInt } from '../../../Utility';
import { AnnouncementAsset, AnnouncementShape, ImageAsset } from '../../../ReachApiV2/endpoints';

function getColorCode(value: string): string {
    const findPoundSign = value.search('#');
    if (findPoundSign !== -1) {
        return value.substring(findPoundSign, findPoundSign + 7);
    }
    return value;
}

class AnnouncementDefault extends AnnouncementBase {
    public declare configuration: AnnouncementAsset | ImageAsset;

    public constructor(configuration: AnnouncementAsset | ImageAsset, widget: AnnouncementWidget) {
        if (configuration.type === 'announcement' && configuration.shapes.length > 0) {
            //format shapes
            configuration.shapes.forEach((shape: AnnouncementShape) => {
                shape.color = getColorCode(shape.color);

                shape.shaperadius =
                    shape.type?.toLowerCase() === 'ellipse'
                        ? '-moz-border-radius:' +
                          `${Math.ceil(shape.width * 0.5)}px / ` +
                          `${Math.ceil(shape.height * 0.5)}px;` +
                          '-webkit-border-radius:' +
                          `${Math.ceil(shape.width * 0.5)}px / ` +
                          `${Math.ceil(shape.height * 0.5)}px;` +
                          'border-radius:' +
                          `${Math.ceil(shape.width * 0.5)}px / ` +
                          `${Math.ceil(shape.height * 0.5)}px;`
                        : '';
            });
        }
        super(configuration, widget, configuration.width, configuration.height);
    }

    protected renderTemplate(): string {
        //todo: better integrate templates
        switch (this.configuration.template) {
            case 'default':
                return defaultTemplate({
                    containerElementId: this.containerElementId,
                    announcement: {
                        name: this.configuration.announcementName,
                        id: this.configuration.announcementId,
                        background_color:
                            this.configuration.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
                        bgimg:
                            this.configuration.bgimg === 'none' || !this.configuration.bgimg
                                ? null
                                : this.configuration.bgimg,
                        textboxes:
                            this.configuration.type === 'announcement'
                                ? this.configuration.textboxes.map((i) => {
                                      return {
                                          id: String(i.id),
                                          animation: i.animation,
                                          x: `${i.x}px`,
                                          y: `${i.y}px`,
                                          width: `${i.width}px`,
                                          height: `${i.height}px`,
                                          z: i.z,
                                          text: i.text ?? '',
                                      };
                                  })
                                : [],
                        shapes:
                            this.configuration.type === 'announcement'
                                ? this.configuration.shapes.map((i) => {
                                      const color = getColorCode(i.color);

                                      const shapeWidth = toInt(i.width);
                                      const shapeHeight = toInt(i.height);

                                      const shaperadius =
                                          i.type.toLowerCase() === 'ellipse'
                                              ? '-moz-border-radius:' +
                                                `${Math.ceil(shapeWidth * 0.5)}px / ` +
                                                `${Math.ceil(shapeHeight * 0.5)}px;` +
                                                '-webkit-border-radius:' +
                                                `${Math.ceil(shapeWidth * 0.5)}px / ` +
                                                `${Math.ceil(shapeHeight * 0.5)}px;` +
                                                'border-radius:' +
                                                `${Math.ceil(shapeWidth * 0.5)}px / ` +
                                                `${Math.ceil(shapeHeight * 0.5)}px;`
                                              : '';

                                      return {
                                          animation: i.animation,
                                          x: `${i.x}px`,
                                          y: `${i.y}px`,
                                          width: `${i.width}px`,
                                          height: `${i.height}px`,
                                          z: i.z,
                                          color,
                                          shaperadius,
                                      };
                                  })
                                : [],
                        images: this.configuration.images.map((i) => {
                            return {
                                animation: i.animation,
                                x: `${i.x}px`,
                                y: `${i.y}px`,
                                width: `${i.width}px`,
                                height: ` ${i.height}px`,
                                z: i.z,
                                url: i.url,
                            };
                        }),
                    },
                    widget: {
                        config: {
                            is_new_media_library: toBoolean(
                                this.widget.config.is_new_media_library,
                                false
                            ),
                        },
                    },
                });
            case 'video-not-supported':
                return videoNotSupportedTemplate({
                    containerElementId: this.containerElementId,
                    announcement: {
                        name: this.configuration.announcementName,
                        id: this.configuration.announcementId,
                        background_color:
                            this.configuration.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
                    },
                });
            case 'insufficient-playlist-rights':
                return insufficientPlaylistRightsTemplate({
                    containerElementId: this.containerElementId,
                    announcement: {
                        name: this.configuration.announcementName,
                        id: this.configuration.announcementId,
                        background_color:
                            this.configuration.backgroundColor ?? DEFAULT_BACKGROUND_COLOR,
                    },
                });
            default:
                throw new Error('incorrect template');
        }
    }

    /**
     * Hides elements.
     */
    removeObject(): void {
        const selector = `.text_${this.configuration.announcementId}, .image_${this.configuration.announcementId}, .shape_${this.configuration.announcementId}`;
        const maybeElements = this.widget.wrapperElement.querySelectorAll<HTMLElement>(selector);

        maybeElements.forEach((element) => {
            if (element.getAttribute('data-element-animation')) {
                element.style.visibility = 'hidden';
            }
        });
    }

    /**
     * announcement initialize method
     *
     * prepares any swf files
     */
    async initialize(): Promise<void> {
        this.removeObject();
    }

    /**
     * set up for preparation
     *
     * reset positions to prepare for animations
     */
    prepPage(): void {
        //make the announcement visible
        const maybeAnnouncementElement = this.widget.wrapperElement.querySelector<HTMLElement>(
            `#announcement_${this.configuration.announcementId}`
        );
        if (maybeAnnouncementElement) {
            maybeAnnouncementElement.style.visibility = 'visible';
        }

        const selector = `.text_${this.configuration.announcementId}, .image_${this.configuration.announcementId}, .shape_${this.configuration.announcementId}`;
        const maybeElements = this.widget.wrapperElement.querySelectorAll<HTMLElement>(selector);

        maybeElements.forEach((element) => {
            const animation = element.getAttribute('data-element-animation');

            const initialWidth = element.getAttribute('data-init-width');
            const initialHeight = element.getAttribute('data-init-height');

            const width = initialWidth ? parseInt(initialWidth) : NaN;
            const height = initialHeight ? parseInt(initialHeight) : NaN;

            if (animation && animation !== '') {
                element.style.visibility = 'visible';
            }

            if (animation === 'fade') {
                //remove shadow til fade is finished -- IE bug
                if (element.style.filter) {
                    element.style.filter = '';
                }

                element.style.opacity = '0.0';
            } else if (animation === 'slide-right') {
                element.style.left = `${-width}px`;
            } else if (animation === 'slide-left') {
                element.style.left = `${this.configuration.width}px`;
            } else if (animation === 'slide-up') {
                element.style.top = `${this.configuration.height}px`;
            } else if (animation === 'slide-down') {
                element.style.top = `${-height}px`;
            }
        });
    }

    /**
     * start animations
     *
     * @TODO: replace animate with keyframes animation.
     */
    beginTransition(): void {
        const selector = `.text_${this.configuration.announcementId}, .image_${this.configuration.announcementId}, .shape_${this.configuration.announcementId}`;
        const maybeElements = this.widget.wrapperElement.querySelectorAll<HTMLElement>(selector);

        maybeElements.forEach((element) => {
            const jqElement = $(element);

            const animation = element.getAttribute('data-element-animation');

            const initialLeft = element.getAttribute('data-init-left');
            const initialTop = element.getAttribute('data-init-top');
            const initialWidth = element.getAttribute('data-init-width');
            const initialHeight = element.getAttribute('data-init-height');

            const left = initialLeft ? parseInt(initialLeft) : NaN;
            const top = initialTop ? parseInt(initialTop) : NaN;
            const width = initialWidth ? parseInt(initialWidth) : NaN;
            const height = initialHeight ? parseInt(initialHeight) : NaN;

            if (animation && animation !== '') {
                element.style.visibility = 'visible';
            }

            if (animation === 'fade') {
                const hasFadeFilter = Boolean(element.style.filter);

                jqElement.animate({ opacity: '1' }, 1000, function () {
                    if (hasFadeFilter) {
                        element.style.filter = 'Shadow(Color=black, Direction=130, Strength=3)';
                    }
                });
            } else if (animation === 'slide-right') {
                $(element).animate({ left: `+=${width + left}` }, 1000);
            } else if (animation === 'slide-left') {
                $(element).animate({ left: `-=${this.configuration.width - left}` }, 1000);
            } else if (animation === 'slide-up') {
                $(element).animate({ top: `-=${this.configuration.height - top}` }, 1000);
            } else if (animation === 'slide-down') {
                $(element).animate({ top: `+=${top + height}` }, 1000);
            }
        });
    }

    /**
     * announcement run page method - start playing
     */
    async runPage(): Promise<void> {
        this.beginTransition();
    }

    async stopPage(): Promise<void> {
        this.removeObject();

        const maybeAnnouncementElement = this.widget.wrapperElement.querySelector<HTMLElement>(
            `#announcement_${this.configuration.announcementId}`
        );
        if (maybeAnnouncementElement) {
            maybeAnnouncementElement.style.visibility = 'visible';
        }
    }
}

export { AnnouncementDefault };
